<template>
  <div
    v-if="open"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
    @click.self="!working && $emit('close')"
  >
    <div class="bg-white rounded-md shadow-lg w-full max-w-md p-4">
      <h3 class="text-base font-semibold mb-2">{{ title }}</h3>
      <div class="text-xs text-gray-600 mb-3">
        Applies to <span class="font-semibold">{{ expenseIds.length }}</span> selected record<span v-if="expenseIds.length !== 1">s</span>.
        The action is logged once per record with the reason below.
      </div>

      <div v-if="mode === 'override_status'" class="mb-3">
        <label class="block text-xs text-gray-500 mb-0.5">New status (applied to all)</label>
        <select v-model="newStatus" class="w-full h-9 px-2 border border-gray-400 rounded-md text-sm">
          <option v-for="s in statusOptions" :key="s.value" :value="s.value">{{ s.label }}</option>
        </select>
      </div>

      <div v-if="mode === 'reassign_manager'" class="mb-3">
        <label class="block text-xs text-gray-500 mb-0.5">New manager (applied to all)</label>
        <select v-model="newManagerId" class="w-full h-9 px-2 border border-gray-400 rounded-md text-sm">
          <option :value="null">— None —</option>
          <option v-for="u in users" :key="u.id" :value="u.id">{{ pickerLabel(u) }}</option>
        </select>
      </div>

      <div class="mb-3">
        <label class="block text-xs text-gray-500 mb-0.5">Reason (required)</label>
        <textarea
          v-model="reason"
          rows="3"
          class="w-full px-2 py-1 border border-gray-400 rounded-md text-sm"
          placeholder="Recorded in each expense's audit log."
        ></textarea>
      </div>

      <div v-if="working" class="text-xs text-gray-600 mb-2">
        Processing {{ progress.done }} / {{ expenseIds.length }}...
        <span v-if="progress.failed > 0" class="text-red-700">
          ({{ progress.failed }} failed so far)
        </span>
      </div>

      <div v-if="errors.length" class="text-xs text-red-700 mb-2 max-h-28 overflow-auto">
        <div class="font-semibold mb-1">Errors:</div>
        <ul class="list-disc ml-4">
          <li v-for="e in errors" :key="e.id">#{{ e.id }}: {{ e.detail }}</li>
        </ul>
      </div>

      <div class="flex items-center justify-end gap-2">
        <button
          class="px-3 py-1 text-sm border border-gray-400 rounded hover:bg-gray-100 disabled:opacity-50"
          :disabled="working"
          @click="$emit('close')"
        >{{ progress.done > 0 ? 'Close' : 'Cancel' }}</button>
        <button
          class="px-3 py-1 text-sm rounded text-white disabled:opacity-50"
          :class="confirmClass"
          :disabled="working || !canConfirm || progress.done > 0"
          @click="apply"
        >{{ working ? 'Working...' : confirmLabel }}</button>
      </div>
    </div>
  </div>
</template>

<script>
export default { name: "admin-bulk-action-modal" };
</script>

<script setup>
import { ref, computed, watch, defineProps, defineEmits, reactive } from "vue";
import { useStore } from "vuex";
import {
  adminOverrideStatus, adminReassignManager, adminSoftDelete,
} from "@/services/Expense.js";

const props = defineProps({
  open: { type: Boolean, default: false },
  mode: { type: String, default: "override_status" },
  expenseIds: { type: Array, default: () => [] },
  users: { type: Array, default: () => [] },
});
const emit = defineEmits(["close", "applied"]);

const store = useStore();

const statusOptions = [
  { value: 'draft', label: 'Draft' },
  { value: 'submitted', label: 'Submitted — Pending Manager Approval' },
  { value: 'manager_approved', label: 'Manager Approved — Pending Accounting' },
  { value: 'accounting_approved', label: 'Accounting Approved' },
  { value: 'closed', label: 'Closed' },
  { value: 'rejected', label: 'Rejected — Returned to Submitter' },
];

const newStatus = ref('draft');
const newManagerId = ref(null);
const reason = ref('');
const working = ref(false);
const errors = ref([]);
const progress = reactive({ done: 0, failed: 0 });

watch(() => [props.open, props.mode], () => {
  if (props.open) {
    reason.value = '';
    errors.value = [];
    progress.done = 0;
    progress.failed = 0;
    newStatus.value = 'draft';
    newManagerId.value = null;
  }
});

const title = computed(() => {
  if (props.mode === 'override_status') return 'Override status — bulk';
  if (props.mode === 'reassign_manager') return 'Reassign manager — bulk';
  if (props.mode === 'soft_delete') return 'Delete records — bulk';
  return 'Bulk admin action';
});

const confirmLabel = computed(() => {
  if (props.mode === 'soft_delete') return `Delete ${props.expenseIds.length}`;
  return `Apply to ${props.expenseIds.length}`;
});

const confirmClass = computed(() => {
  if (props.mode === 'soft_delete') return 'bg-red-600 hover:bg-red-700';
  return 'bg-accent-primary hover:bg-accent-secondary';
});

const canConfirm = computed(() => !!reason.value.trim() && props.expenseIds.length > 0);

function pickerLabel(u) {
  const name = `${u.first_name || ''} ${u.last_name || ''}`.trim();
  if (name && u.username) return `${name} (${u.username})`;
  return name || u.username || `User #${u.id}`;
}

async function apply() {
  if (!canConfirm.value) return;
  const token = store.state.auth.token;
  const r = reason.value.trim();
  working.value = true;
  errors.value = [];
  progress.done = 0;
  progress.failed = 0;
  // Run sequentially so failure messages are easy to associate with an
  // id, and so we don't burst the API.
  for (const id of props.expenseIds) {
    try {
      if (props.mode === 'override_status') {
        await adminOverrideStatus(token, id, newStatus.value, r);
      } else if (props.mode === 'reassign_manager') {
        await adminReassignManager(token, id, newManagerId.value, r);
      } else if (props.mode === 'soft_delete') {
        await adminSoftDelete(token, id, r);
      }
    } catch (e) {
      progress.failed += 1;
      errors.value = [
        ...errors.value,
        { id, detail: e?.response?.data?.detail || 'Failed.' },
      ];
    }
    progress.done += 1;
  }
  working.value = false;
  emit('applied', {
    total: props.expenseIds.length,
    succeeded: props.expenseIds.length - progress.failed,
    failed: progress.failed,
  });
}
</script>
